'use strict';
import React, { useEffect, useMemo, useState, FC, ReactElement, useRef, useCallback } from 'react';
import {
  ColumnSort,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  Row,
  CellContext,
  ColumnOrderState,
  Header,
  Table,
  getFilteredRowModel,
  getExpandedRowModel,
  BuiltInSortingFn,
  SortingFn,
  ExpandedState,
  RowData
} from '@tanstack/react-table';
import { QueryClient, QueryClientProvider, useInfiniteQuery } from '@tanstack/react-query';
import { default as theme } from 'themes';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import camelCase from 'lodash/camelCase';
import { cloneDeep, isEmpty } from 'lodash';
import { BaseType, ModalSize, TableColumnConfigs } from 'types';
import {
  Typography,
  Modal,
  Checkbox,
  Button,
  StyledColumnRefContainer,
  StyledNonDraggableColumnRefContainer,
  StyledDragRefContainer,
  OptionsModalContainer,
  SelectionHeader,
  OptionalColumnContainer,
  OptionColumnCheckboxWrapper,
  OptionalColumnName,
  SelectAllOptionalColumn,
  StyledRootContainer,
  Wrapper,
  ColumnOption,
  OptionWrapper,
  ColumnOptionWrapper
} from 'components';
//column cofig
import { useTranslation } from 'react-i18next';
import { convertCamelCaseintoTitleCase } from 'common/helpers/v1';
import styled, { CSSProperties } from 'styled-components';
import breakpoints from 'constants/breakpoints';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import IcoTableColumnOption from 'icons/indexed-icons/IcoTableColumnOption';
import IntersectionObserverWrapper from 'components/NewBaseTable/IntersectionObserverWrapper';
import { useMediaQuery } from 'react-responsive';
import { Backdrop, ModalButtonContainer, ModalContainer } from './ProcessTableWrapperWidget.elements';

type sortUndefinedType = -1 | 'first' | 'last' | 1 | false;
export interface ColumnConfig extends BaseType {
  id: string;
  header: string | ReactElement;
  isEnableSorting: boolean;
  isSelected: boolean;
  renderer: (cellValue: CellContext<TableColumnConfigs, string>) => JSX.Element | React.ReactNode;
  customSort?: BuiltInSortingFn | SortingFn<TableColumnConfigs>;
  disableGlobalSearch?: boolean;
  sortUndefined?: sortUndefinedType;
}
interface SortStateItem extends BaseType {
  id: string;
  desc: boolean;
}

export type SortState = SortStateItem | SortStateItem[];
interface NewBaseTableProps {
  newTableData: TableColumnConfigs[];
  sortState: SortState | SortState[];
  isShowColumnOptions?: boolean;
  removeColumnOption?: boolean;
  shadow?: boolean;
  customClassName?: string;
  disableVirtualize?: boolean;
  getRowStyles?: (row: Row<TableColumnConfigs>) => CSSProperties;
  handleInlineEditing?: (formData?: Record<string, string | number | boolean>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch?: LazyQueryTrigger<any>;
  enableServerSide?: boolean;
  ApiFetchSize?: number;
  columnConfigs: ColumnConfig[];
  changeColumn:(name: string| string[]) => void;
  getProcessLogColumnOrder:(column: ColumnOrderState) => void;
}

interface OptionalColumnsState {
  key: string;
  name: string;
  isSelected?: boolean;
  meta?: { [key: string]: boolean | string } | (() => { [key: string]: boolean | string });
}
type RowDataResponse = {
  data: TableColumnConfigs[];

  meta: {
    totalRowCount: number;
  };
};

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    getRowStyles: (row: Row<TData>) => CSSProperties;
  }
}

const { size } = breakpoints;

const keyPrefix = 'health-data';
const columnHelper = createColumnHelper<TableColumnConfigs>();

const addKeysToData = (keyPrefix: string, data: TableColumnConfigs[]) => {
  return data.map((item) => {
    return {
      ...item,
      key: `${keyPrefix}-${item.id}`
    };
  });
};

const reorderColumn = (draggedId: string, targetId: string, columnOrder: string[]) => {
  const newOrder = [...columnOrder];
  const draggedIndex = newOrder.indexOf(draggedId);
  const targetIndex = newOrder.indexOf(targetId);

  if (draggedIndex !== -1 && targetIndex !== -1) {
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedId);
  }

  return newOrder;
};

const DraggableColumnHeader: FC<{
  header: Header<TableColumnConfigs, unknown>;
  table: Table<TableColumnConfigs>;
  tableColumns: OptionalColumnsState[];
  handleOptionChange: (name?: string, isSelected?: boolean) => void;
  getProcessLogColumnOrder:(column:ColumnOrderState)=>void;
}> = ({ header, table, tableColumns, handleOptionChange, getProcessLogColumnOrder}) => {
  const { getState, setColumnOrder } = table;
  const { columnOrder } = getState();
  const { column } = header;

  const dragItem = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', column.id);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    setDragOverId(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Required to allow a drop
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain');
    const targetId = column.id;

    if (draggedId !== targetId) {
      const newOrder = reorderColumn(draggedId, targetId, columnOrder);
      setColumnOrder(newOrder);
      getProcessLogColumnOrder(newOrder);
    }
  };

  return (
    <StyledColumnRefContainer
      style={{ opacity: dragItem.current === column.id ? 0.5 : 1 }}
      key={header.id}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
        {header.isPlaceholder ? null : (
          <StyledDragRefContainer>
            <OptionColumnCheckboxWrapper
              key={header.column.id}
              style={{
                backgroundColor: dragOverId === column.id ? 'lightgrey' : 'white',
              }}
            >
              <Checkbox
                key={header.column.id}
                id={`${header.column.id}-${header.index}`}
                width={20}
                height={20}
                checked={
                  tableColumns?.find((tableColumn) => tableColumn.key === column.id)?.isSelected
                }
                disabled={
                  tableColumns?.find((tableColumn) => tableColumn.key === column.id)?.meta
                    ?.isAlwaysVisible
                }
                onChange={() => {
                  const opt = tableColumns?.find((tableColumn) => tableColumn.key === column.id);
                  handleOptionChange(opt?.name, opt?.isSelected);
                }}
              />
              <OptionalColumnName>
                {convertCamelCaseintoTitleCase(header.column.id)}
              </OptionalColumnName>
              <FontAwesomeIcon
                icon={faGripVertical}
                color={theme.colors.darkBlue}
                style={{ marginLeft: 'auto', marginTop: 'auto', marginBottom: 'auto' }}
                size="xs"
              />
            </OptionColumnCheckboxWrapper>
          </StyledDragRefContainer>
        )}
    </StyledColumnRefContainer>
  );
};

export const TableContainer = styled.div`
  width: auto;
  z-index: 5;
  margin-top: 0.5rem;
  @media (min-width: 2160px) {
    max-height: 1442rem;
  }
`;
export const FetchDetail = styled.div`
  color: #0076cc;
  margin-top: 12px;
  margin-bottom: -6px;
  padding-left: 10px;
`;

function isAllColumnOptionsVisible(array: OptionalColumnsState[]) {
  // Iterate through each object in the array
  for (let i = 0; i < array.length; i++) {
    // Check if isSelected property is false
    if (!array[i].isSelected) {
      return false; // If any object's isSelected is false, return false
    }
  }
  return true; // If all objects' isSelected are true, return true
}

const TanstackTable = (props: NewBaseTableProps): JSX.Element => {
  const { t } = useTranslation(['common']);
  const {
    newTableData,
    sortState,
    customClassName,
    disableVirtualize,
    handleInlineEditing,
    removeColumnOption,
    refetch,
    shadow,
    enableServerSide,
    ApiFetchSize,
    isShowColumnOptions,
    columnConfigs,
    changeColumn,
    getProcessLogColumnOrder
  } = props;

  const [dataTable, setDataTable] = useState(() => (newTableData ? [...newTableData] : []));
  const [originalData, setOriginalData] = useState(() => (newTableData ? [...newTableData] : []));
  const [editedRows, setEditedRows] = useState({});

  const updatedSortState = Array.isArray(sortState) ? sortState : [sortState];
  const [sorting, setSorting] = useState<SortingState>(updatedSortState);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showColumnOptions, setShowColumnOptions] = useState<boolean>(false);
  const [tableColumns, setTableColumns] = useState<OptionalColumnsState[]>([]);
  const [tempTableColumns, setTempTableColumns] = useState<OptionalColumnsState[]>([]);
  const [selectAllOptionalColumns, setSelectAllOptionalColumns] = useState<boolean>(
    tableColumns.length !== 0 && isAllColumnOptionsVisible(tableColumns)
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const [globalFilter, setGlobalFilter] = React.useState('');
  const isDesktopOrLaptop = useMediaQuery({ minWidth: size.xl + 1 });

  const searchableCol = useMemo(() => {
    return columnConfigs?.filter((columnConfigs) => {
        return !columnConfigs.disableGlobalSearch;
      })
      .map((data) => data.id);
  }, []);

  // let columns = columnConfigs?.map((columnConfig) => {
  let columns =
  columnConfigs?.map((columnConfig) => {
    
      const metaData = {};
      //Backward compatibility for old column configs
      metaData['isAlwaysVisible'] = columnConfig?.isAlwaysVisible || false;
      //Dynamicly set keys and values that are stores in meta object
      for (const [key, value] of Object.entries(columnConfig.meta || {})) {
        metaData[key] = value;
      }

      return columnHelper.accessor(columnConfig?.id, {
        id: columnConfig.id,
        header: () => columnConfig.header,
        cell: (info: CellContext<TablecolumnConfig, string> | JSX.Element) => {
          if (typeof columnConfig.cell === 'function') {
            const Cell = columnConfig.cell;
            return <Cell {...info} />;
          } else {
            return <span className="cell">{columnConfig.renderer(info)}</span>;
          }
        },
        enableSorting: columnConfig.isEnableSorting,
        sortUndefined: columnConfig.sortUndefined,
        meta: metaData,
        enableGlobalFilter: searchableCol?.includes(columnConfig.id),
        sortingFn: columnConfig.customSort || 'basic',
        ...(columnConfig.mobileViewCustomCellType
          ? { mobileViewCustomCellType: columnConfig.mobileViewCustomCellType }
          : {})
      });
    }) || [];

  const TotalFetchSize = enableServerSide && ApiFetchSize ? ApiFetchSize : 20;

  const fetchSize = !disableVirtualize ? TotalFetchSize : newTableData?.length;

  useEffect(() => {
    columns.forEach((opt) => {
      const targetCol = columnConfigs?.find((colConfig) => colConfig.id === opt.id);
      tableColumns.push({
        key: camelCase(opt.id),
        name: opt.id || '',
        isSelected: targetCol?.isSelected,
        meta: { ...opt?.meta }
      });
    });

    setSelectAllOptionalColumns(isAllColumnOptionsVisible(tableColumns));
  }, []);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columns?.map((column) => column.id as string)
  );

  const tableData = useMemo(() => {
    if (!newTableData?.length) return [];

    return addKeysToData(keyPrefix, newTableData);
  }, [newTableData]);

  const onCloseCommentModal = () => {
    /* eslint-disable  @typescript-eslint/no-non-null-assertion */
    setTableColumns(tempTableColumns);
    setShowColumnOptions!(false);
  };

  const handleOptionChange = (name?: string, isSelected?: boolean) => {
    changeColumn(name||"")
    
    tableColumns.forEach((col) => {
      if (col.name === name) {
        col.isSelected = !isSelected;
      }
    });
    setSelectAllOptionalColumns(isAllColumnOptionsVisible(tableColumns));
    setTableColumns([...tableColumns]);
  };

  const handleSelectAllOptionsChange = () => {

    tableColumns
      ?.filter((col) => col.meta?.isAlwaysVisible === false)
      ?.forEach((col) => {
        col.isSelected = !selectAllOptionalColumns;
      });
    
    const removedCol =  tableColumns.filter((col) => !col.isSelected).map((col) => col.name)
    changeColumn(removedCol);
        
    setTableColumns([...tableColumns]);
    setSelectAllOptionalColumns(isAllColumnOptionsVisible(tableColumns));
  };

  if (!isEmpty(tableColumns)) {
    columns = columns.filter((column) => {
      const compareItem = tableColumns.find(
        (tableColumn) => tableColumn.key === camelCase(column.id)
      );
      return compareItem && compareItem.isSelected;
    });
  }

  const triggerShowColumnOptions = (): void => {
    const newTableColumns = cloneDeep(tableColumns);


    const activeColumns: OptionalColumnsState[] = [];
    newTableColumns.forEach((col) => {
      if (col.key !== 'isDummy') activeColumns.push(col);
    });
    setTempTableColumns(newTableColumns);
    setTableColumns(activeColumns);
    setShowColumnOptions(true);
  };
  // get row data
  const fetchData = async (
    start: number,
    size: number,
    sorting: SortingState
  ): Promise<{
    data: TableColumnConfigs[];
    meta: {
      totalRowCount: number;
    };
  }> => {
    const dbData = [...tableData];
    const sort = sorting?.[0] as ColumnSort;

    if (sort) {
      const { id, desc } = sort as { id: string; desc: boolean };

      if (id) {
        dbData.sort((a, b) => {
          if (desc) {
            return String(b[id]).localeCompare(String(a[id]), undefined, { caseFirst: 'lower' });
          }
          return String(a[id]).localeCompare(String(b[id]), undefined, { caseFirst: 'lower' });
        });
      }
    }

    /*** Function for calling API after scroll reached end of table */
    if (enableServerSide && refetch) {
      const { data } = await refetch({
        limit: fetchSize,
        offset: start,
        search: globalFilter
      });
      return {
        data: data?.prosealStatuses || [],
        meta: {
          totalRowCount: Number(data?.totalCount)
        }
      };
    }

    return {
      data: dbData.slice(start, start + size),
      meta: {
        totalRowCount: dbData?.length
      }
    };
  };
  //react-query has an useInfiniteQuery hook just for this situation!
  const { data, fetchNextPage, isFetching } = useInfiniteQuery<RowDataResponse>(
    ['table-data', sorting, globalFilter, newTableData], //adding sorting state as key causes table to reset and fetch from new beginning upon sort
    async ({ pageParam = 0 }) => {
      const start = pageParam * fetchSize;
      if (sorting.length === 0) {
        const fetchedData = fetchData(start, fetchSize, updatedSortState); //pretend api call
        return fetchedData;
      } else {
        const fetchedData = fetchData(start, fetchSize, sorting); //pretend api call
        return fetchedData;
      }
    },
    {
      getNextPageParam: (_lastGroup, groups) => groups.length,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  );

  //we must flatten the array of arrays from the useInfiniteQuery hook
  const flatData = useMemo(() => data?.pages?.flatMap((page) => page.data) ?? [], [data]);

  const totalDBRowCount = data?.pages?.[0]?.meta?.totalRowCount ?? 0;
  const totalFetched = flatData.length;
  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        const onScrollReach = enableServerSide ? 1000 : 300;
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (
          scrollHeight - scrollTop - clientHeight < onScrollReach &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  );

  //a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const table = useReactTable({
    data: flatData,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      globalFilter,
      expanded
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    meta: {
      editedRows,
      setEditedRows,
      revertCellData: (rowIndex: number, revert: boolean) => {
        if (revert) {
          setDataTable((old) =>
            old.map((row, index) => (index === rowIndex ? originalData[rowIndex] : row))
          );
        } else {
          setOriginalData((old) =>
            old.map((row, index) => (index === rowIndex ? dataTable[rowIndex] : row))
          );
        }
      },
      updateRow: (data: Record<string, unknown>) => {
        handleInlineEditing && handleInlineEditing(data);
      }
    }
  });
  const tableContainerRef = useRef<HTMLDivElement>(null);

   return (
    <StyledRootContainer className={customClassName}>
      {removeColumnOption ? null : (
        <>
          <ColumnOption shadow={shadow} className="customcss">
            <IntersectionObserverWrapper>
              <Wrapper data-targetid="column-options">
                {!isShowColumnOptions && (
                  <OptionWrapper
                    onClick={() => {
                      triggerShowColumnOptions!();
                    }}
                  >
                    <ColumnOptionWrapper className="table-header-button">
                      <IcoTableColumnOption width="16" />
                      <span>
                        {!isDesktopOrLaptop ? (
                          <>{t('Column options')}</>
                        ) : (
                          <>
                            {t('Column options')} (
                            {
                              table
                                .getHeaderGroups()[0]
                                .headers.filter((head) => head.id !== 'action').length
                            }{' '}
                            {t('Columns')})
                          </>
                        )}
                      </span>
                    </ColumnOptionWrapper>
                  </OptionWrapper>
                )}
              </Wrapper>
            </IntersectionObserverWrapper>
          </ColumnOption>
        </>
      )}
      {showColumnOptions && (
      <>
      <Backdrop onClick={onCloseCommentModal}/>
        <ModalContainer>
      <Modal
        onClose={onCloseCommentModal}
        size={ModalSize.XXSMALL_AUTO_HEIGHT}
        title="Column Options"
        visible={showColumnOptions as boolean}
        widthOverride="25rem"

      >
        <OptionsModalContainer>
          <SelectionHeader>
            <Typography variant="body2" color={theme.colors.darkGrey}>
              Select columns to show
            </Typography>
          </SelectionHeader>

          <OptionalColumnContainer>
            {table?.getHeaderGroups()?.map((headerGroup) => (
              <div key={headerGroup.id}>
                {headerGroup?.headers?.map(
                  (header) =>
                    header.id !== 'action' && (
                      <DraggableColumnHeader
                        key={header.id}
                        header={header}
                        table={table}
                        tableColumns={tableColumns}
                        handleOptionChange={handleOptionChange}
                        getProcessLogColumnOrder={getProcessLogColumnOrder}
                      />
                    )
                )}
              </div>
            ))}

            {tableColumns?.map((opt, index) => {
              const headerGroup = table.getHeaderGroups()[0];
              const headers = headerGroup?.headers;
              const header = headers?.find((header) => header.id === opt.name);
             

              return header ? (
                ''
              ) : (
                <StyledNonDraggableColumnRefContainer key={index}>
                  <OptionColumnCheckboxWrapper>
                    <Checkbox
                      key={index}
                      id={`${opt.name}-${index}`}
                      width={20}
                      height={20}
                      checked={opt.isSelected}
                      onChange={() => {
                        handleOptionChange(opt.name, opt.isSelected);
                      }}
                    />
                    <OptionalColumnName>
                      {convertCamelCaseintoTitleCase(t(opt.name))}
                    </OptionalColumnName>
                  </OptionColumnCheckboxWrapper>
                </StyledNonDraggableColumnRefContainer>
              );
            })}
          </OptionalColumnContainer>
          <SelectAllOptionalColumn>
            <Checkbox
              id={`opt-all`}
              width={20}
              height={20}
              checked={selectAllOptionalColumns}
              onChange={() => {
                handleSelectAllOptionsChange();
              }}
            />
            <OptionalColumnName htmlFor="checkbox-opt-all">Select All</OptionalColumnName>
          </SelectAllOptionalColumn>
        </OptionsModalContainer>
        <ModalButtonContainer>
        <Button onClick={onCloseCommentModal} variant="outline-blue" size="small">
            Close
          </Button>
          {/* <Button variant="filled-primary" size="small">
            Save
          </Button> */}
        </ModalButtonContainer>
      </Modal>
      </ModalContainer></>
      )}
    </StyledRootContainer>
  );
};
const DraggableColumnOptionHeader = (props: NewBaseTableProps): JSX.Element => {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <TanstackTable {...props} />
    </QueryClientProvider>
  );
};

export default DraggableColumnOptionHeader;

